/**
 * Before/After Click & Hold Component
 * Handles mouse, touch, and keyboard interactions
 */

class BeforeAfterClickHold {
  constructor(element) {
    this.element = element;
    this.isRevealing = false;
    this.isPreloaded = false;
    this.observer = null;
    
    this.init();
  }

  init() {
    // Preload after image when component enters viewport
    this.setupIntersectionObserver();
    
    // Mouse events
    this.element.addEventListener('mousedown', this.handleMouseDown.bind(this));
    this.element.addEventListener('mouseup', this.handleMouseUp.bind(this));
    this.element.addEventListener('mouseleave', this.handleMouseLeave.bind(this));
    
    // Touch events
    this.element.addEventListener('touchstart', this.handleTouchStart.bind(this), { passive: true });
    this.element.addEventListener('touchend', this.handleTouchEnd.bind(this));
    this.element.addEventListener('touchcancel', this.handleTouchCancel.bind(this));
    
    // Keyboard events
    this.element.addEventListener('keydown', this.handleKeyDown.bind(this));
    this.element.addEventListener('keyup', this.handleKeyUp.bind(this));
    
    // Prevent context menu on long press
    this.element.addEventListener('contextmenu', (e) => {
      if (this.isRevealing) {
        e.preventDefault();
      }
    });
  }

  setupIntersectionObserver() {
    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !this.isPreloaded) {
            this.preloadAfterImage();
          }
        });
      }, {
        rootMargin: '50px'
      });
      
      this.observer.observe(this.element);
    } else {
      // Fallback for browsers without IntersectionObserver
      this.preloadAfterImage();
    }
  }

  preloadAfterImage() {
    const afterImage = this.element.querySelector('.ba-after-layer img[data-preload-src]');
    
    if (afterImage && !this.isPreloaded) {
      this.isPreloaded = true;
      const preloadSrc = afterImage.dataset.preloadSrc;
      
      if (preloadSrc) {
        const img = new Image();
        img.src = preloadSrc;
        
        // Optional: show loading state
        this.element.classList.add('is-loading');
        
        img.onload = () => {
          this.element.classList.remove('is-loading');
        };
        
        img.onerror = () => {
          this.element.classList.remove('is-loading');
          console.error('Failed to preload after image');
        };
      }
    }
  }

  reveal() {
    if (!this.isRevealing) {
      this.isRevealing = true;
      this.element.classList.add('is-revealing');
      
      // Trigger haptic feedback on supported devices
      if (navigator.vibrate) {
        navigator.vibrate(10);
      }
    }
  }

  hide() {
    if (this.isRevealing) {
      this.isRevealing = false;
      this.element.classList.remove('is-revealing');
    }
  }

  // Mouse handlers
  handleMouseDown(e) {
    e.preventDefault();
    this.reveal();
  }

  handleMouseUp(e) {
    this.hide();
  }

  handleMouseLeave(e) {
    this.hide();
  }

  // Touch handlers
  handleTouchStart(e) {
    this.reveal();
  }

  handleTouchEnd(e) {
    this.hide();
  }

  handleTouchCancel(e) {
    this.hide();
  }

  // Keyboard handlers
  handleKeyDown(e) {
    // Space or Enter key
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      this.reveal();
    }
  }

  handleKeyUp(e) {
    // Space or Enter key
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      this.hide();
    }
  }

  destroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }
}

// Initialize all before/after components
function initBeforeAfterComponents() {
  const containers = document.querySelectorAll('[data-before-after]');
  
  containers.forEach(container => {
    if (!container.beforeAfterInstance) {
      container.beforeAfterInstance = new BeforeAfterClickHold(container);
    }
  });
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initBeforeAfterComponents);
} else {
  initBeforeAfterComponents();
}

// Re-initialize when Shopify section is loaded/reloaded (theme editor)
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', initBeforeAfterComponents);
  document.addEventListener('shopify:section:reorder', initBeforeAfterComponents);
}

// Export for potential external use
if (typeof module !== 'undefined' && module.exports) {
  module.exports = BeforeAfterClickHold;
}

